<template>
  <div class="draft-history">
    <v-skeleton-loader v-if="loading" type="list-item-two-line@3" class="transparent" />

    <div v-else-if="!normalisedDrafts.length" class="ds-empty-state">
      <v-icon class="ds-empty-state__icon">mdi-history</v-icon>
      <div class="ds-empty-state__title">No draft history yet</div>
      <div class="ds-empty-state__description">
        Every save or AI regeneration adds a version here.
      </div>
    </div>

    <v-list v-else dense two-line color="transparent" class="py-0">
      <v-list-item
        v-for="entry in normalisedDrafts"
        :key="entry.key"
        class="draft-history__item"
        :class="{ 'draft-history__item--current': entry.isCurrent }"
        @click="$emit('preview', entry.draft)">
        <v-list-item-content>
          <v-list-item-title class="d-flex align-center flex-wrap">
            <span class="draft-history__version">{{ entry.versionLabel }}</span>
            <v-chip
              v-if="entry.origin"
              x-small
              class="ml-2"
              :color="entry.origin.color"
              text-color="white">
              {{ entry.origin.label }}
            </v-chip>
            <v-chip v-if="entry.isCurrent" x-small class="ml-2" color="primary" text-color="white">
              Current
            </v-chip>
          </v-list-item-title>
          <v-list-item-subtitle class="draft-history__meta">
            <span v-if="entry.author">{{ entry.author }} · </span>
            <span>{{ entry.absoluteDate }}</span>
            <span v-if="entry.relativeDate" class="draft-history__relative">
              ({{ entry.relativeDate }})</span
            >
          </v-list-item-subtitle>
        </v-list-item-content>

        <v-list-item-action v-if="!entry.isCurrent">
          <v-tooltip left>
            <template #activator="{ on, attrs }">
              <v-btn icon small v-bind="attrs" v-on="on" @click.stop="$emit('revert', entry.draft)">
                <v-icon small>mdi-restore</v-icon>
              </v-btn>
            </template>
            <span>Revert to this version</span>
          </v-tooltip>
        </v-list-item-action>
      </v-list-item>
    </v-list>
  </div>
</template>

<script>
  import dayjs from 'dayjs';
  import relativeTime from 'dayjs/plugin/relativeTime';
  import { $formatDate } from '@Utils';

  dayjs.extend(relativeTime);

  // Origin labels are matched case-insensitively against whatever the draft DTO
  // reports. Anything unrecognised renders no badge at all — a wrong badge is worse
  // than none, and the draft DTO's origin field is not contract-verified.
  const ORIGIN_BADGES = {
    ai: { label: 'AI', color: 'info' },
    generated: { label: 'AI', color: 'info' },
    generation: { label: 'AI', color: 'info' },
    human: { label: 'Human', color: 'grey darken-1' },
    manual: { label: 'Human', color: 'grey darken-1' },
    user: { label: 'Human', color: 'grey darken-1' },
    edit: { label: 'Human', color: 'grey darken-1' },
    revert: { label: 'Revert', color: 'warning' },
    reverted: { label: 'Revert', color: 'warning' },
  };

  export default {
    name: 'DraftHistoryList',
    props: {
      drafts: {
        type: Array,
        default: () => [],
      },
      loading: {
        type: Boolean,
        default: false,
      },
      currentDraftId: {
        type: [String, Number],
        default: null,
      },
    },
    computed: {
      normalisedDrafts() {
        return (this.drafts || []).map((draft, index) => {
          const id = this.draftId(draft);
          const version = this.draftVersion(draft);
          const timestamp = this.draftTimestamp(draft);
          return {
            draft,
            key: id === null || id === undefined ? `draft-${index}` : String(id),
            versionLabel:
              version === null || version === undefined
                ? `Version ${index + 1}`
                : `Version ${version}`,
            origin: this.originBadge(draft),
            author: this.draftAuthor(draft),
            absoluteDate: timestamp ? $formatDate(timestamp, 'DD/MM/YYYY HH:mm') : 'Unknown date',
            relativeDate: timestamp && dayjs(timestamp).isValid() ? dayjs(timestamp).fromNow() : '',
            isCurrent:
              this.currentDraftId !== null &&
              this.currentDraftId !== undefined &&
              id !== null &&
              id !== undefined &&
              String(id) === String(this.currentDraftId),
          };
        });
      },
    },
    methods: {
      draftId(draft) {
        // `id` is the documented shape; `draftId`/`Id` are probed because the drafts list
        // DTO casing is not verified (see plan addendum §A.5).
        const candidates = [draft.id, draft.draftId, draft.Id, draft.DraftId];
        return candidates.find((value) => value !== null && value !== undefined);
      },
      draftVersion(draft) {
        const candidates = [draft.version, draft.Version, draft.versionNumber];
        return candidates.find((value) => value !== null && value !== undefined);
      },
      draftAuthor(draft) {
        const candidates = [
          draft.createdByName,
          draft.authorName,
          draft.createdByUserName,
          draft.author,
          draft.createdBy,
        ];
        const found = candidates.find(
          (value) => typeof value === 'string' && value.trim().length > 0,
        );
        return found ? found.trim() : '';
      },
      draftTimestamp(draft) {
        const candidates = [draft.createdAt, draft.createdOn, draft.created, draft.modifiedAt];
        return candidates.find((value) => !!value);
      },
      originBadge(draft) {
        // `origin` is the field the plan names; `source` and `createdBy` are probed as
        // fallbacks. `createdBy` is only consulted when it reads as an origin keyword
        // rather than a person's name, so a human author never becomes a badge.
        const raw = [draft.origin, draft.Origin, draft.source, draft.Source, draft.createdBy].find(
          (value) => typeof value === 'string' && value.trim().length > 0,
        );
        if (!raw) return null;
        const key = raw.trim().toLowerCase();
        return ORIGIN_BADGES[key] || null;
      },
    },
  };
</script>

<style scoped lang="scss">
  .draft-history__item {
    border-bottom: 1px solid var(--ds-border-subtle, rgba(255, 255, 255, 0.08));

    &:last-child {
      border-bottom: none;
    }
  }

  .draft-history__item--current {
    background: rgba(3, 98, 76, 0.12);
    border-left: 2px solid var(--v-primary-base);
  }

  .draft-history__version {
    font-weight: 600;
    font-size: 0.875rem;
  }

  .draft-history__meta {
    font-size: 0.75rem;
  }

  .draft-history__relative {
    opacity: 0.7;
  }
</style>
